import { EGE_H3_ZOOM_THRESHOLD } from '@const';
import { booleanIntersects } from '@turf/boolean-intersects';
import { booleanPointInPolygon } from '@turf/boolean-point-in-polygon';
import { point, polygon } from '@turf/helpers';
import type { EGESlimCachePBFResponseRow, EGESuperSummarizedPBFExt } from '@types';
import { interpolateBlues } from 'd3-scale-chromatic';
import dayjs from 'dayjs';
import type { LngLat } from 'react-map-gl/mapbox';
import { isEGESuperSummarizedPBFExtArray } from '../utils-types';

/**
 * Generates 5 color shades from a hex color (light to dark)
 * Creates better visual distinction by varying lightness in HSL color space
 */
export const createColorShades = (hexColor: string): [number, number, number, number][] => {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Convert hex to RGB
  let r = parseInt(hex.substring(0, 2), 16) / 255;
  let g = parseInt(hex.substring(2, 4), 16) / 255;
  let b = parseInt(hex.substring(4, 6), 16) / 255;

  // Convert RGB to HSL
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  // Generate 5 shades with different lightness values
  // From lighter to darker with good visual separation
  const lightnessValues = [0.8, 0.65, 0.5, 0.35, 0.22];

  const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
    let r, g, b;

    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
  };

  return lightnessValues.map((lightness) => {
    const [r, g, b] = hslToRgb(h, s, lightness);
    return [r, g, b, 100];
  });
};

/**
 * Determines if the map should display individual points based on zoom level.
 * @param zoom - Current map zoom level
 * @returns true if zoom is at or above threshold (show points), false otherwise (show H3 hexagons)
 */
export const shouldShowPoints = (zoom: number | undefined): boolean => {
  return (zoom ?? 0) >= EGE_H3_ZOOM_THRESHOLD;
};

/**
 * Determines if the map should display H3 hexagon aggregation based on zoom level.
 * @param zoom - Current map zoom level
 * @returns true if zoom is below threshold (show H3), false otherwise (show points)
 */
export const shouldShowH3 = (zoom: number | undefined): boolean => {
  return (zoom ?? 0) < EGE_H3_ZOOM_THRESHOLD;
};

export const getColor = (d: number): [number, number, number, number] => {
  const hex = interpolateBlues(d / 5);
  // parse "rgb(10,10,10)" into an array:
  const rgb = hex
    .substring(4, hex.length - 1)
    .split(',')
    .map((d) => parseInt(d, 10));
  return [...rgb, 128] as [number, number, number, number];
};

export const calculateArea = (startLng: { lat: number; lng: number }, currentLng: { lat: number; lng: number }): number => {
  // Calculate the area of the bounding box created by start & current lnglat:
  const R = 6371; // Radius of the Earth in km

  // Convert degrees to radians
  const lat1 = (startLng.lat * Math.PI) / 180;
  const lat2 = (currentLng.lat * Math.PI) / 180;
  const dLat = lat2 - lat1;
  const dLon = ((currentLng.lng - startLng.lng) * Math.PI) / 180;

  // Calculate north-south distance (height)
  const height = Math.abs(dLat * R);

  // Calculate east-west distance (width) at the average latitude
  const avgLat = (lat1 + lat2) / 2;
  const width = Math.abs(dLon * Math.cos(avgLat) * R);

  return width * height; // Actual rectangular area in km²
};

/**
 * Checks if a point is inside a polygon using turf's booleanPointInPolygon.
 * @param pt - Object with latitude and longitude
 * @param polygonCoords - Array of polygon vertices (LngLat objects)
 * @returns true if point is within polygon, false otherwise
 */
export const isPointInPolygon = (pt: { latitude: number; longitude: number }, polygonCoords: LngLat[]): boolean => {
  if (polygonCoords.length < 3) return false;

  // Create turf point [longitude, latitude]
  const turfPoint = point([pt.longitude, pt.latitude]);

  // Create turf polygon - need to close the ring by adding first point at the end
  const coords = [...polygonCoords.map((c) => [c.lng, c.lat]), [polygonCoords[0].lng, polygonCoords[0].lat]];
  const turfPolygon = polygon([coords]);

  return booleanPointInPolygon(turfPoint, turfPolygon);
};

/**
 * Represents a bounding box with min/max coordinates
 */
export interface BoundingBox {
  minLng: number;
  maxLng: number;
  minLat: number;
  maxLat: number;
}

type WktCoordinate = [number, number];
type WktPolygonCoordinates = WktCoordinate[][];

const stripOuterParens = (text: string): string | null => {
  const trimmed = text.trim();
  if (!trimmed.startsWith('(') || !trimmed.endsWith(')')) return null;
  return trimmed.slice(1, -1);
};

const splitTopLevelCommaSections = (text: string): string[] => {
  const sections: string[] = [];
  let depth = 0;
  let current = '';

  for (const char of text) {
    if (char === '(') {
      depth += 1;
    } else if (char === ')') {
      depth -= 1;
    }

    if (char === ',' && depth === 0) {
      sections.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  if (current.trim()) {
    sections.push(current.trim());
  }

  return sections;
};

const parseWktRing = (ringText: string): WktCoordinate[] | null => {
  const innerRing = stripOuterParens(ringText);
  if (!innerRing) return null;

  const coordinates = innerRing
    .split(',')
    .map((pair) => pair.trim())
    .filter(Boolean)
    .map((pair) => {
      const [lngText, latText] = pair.split(/\s+/);
      const longitude = Number(lngText);
      const latitude = Number(latText);

      if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
        throw new Error('Invalid WKT coordinate');
      }

      return [longitude, latitude] as WktCoordinate;
    });

  return coordinates.length > 0 ? coordinates : null;
};

const parseWktPolygon = (polygonText: string): WktPolygonCoordinates | null => {
  const innerPolygon = stripOuterParens(polygonText);
  if (!innerPolygon) return null;

  const rings = splitTopLevelCommaSections(innerPolygon)
    .map((ringText) => parseWktRing(ringText))
    .filter((ring): ring is WktCoordinate[] => Boolean(ring));

  return rings.length > 0 ? rings : null;
};

export const parseWKTPolygons = (wkt: string): WktPolygonCoordinates[] | null => {
  const normalizedWkt = wkt.trim();

  try {
    if (normalizedWkt.startsWith('POLYGON')) {
      const polygon = parseWktPolygon(normalizedWkt.slice('POLYGON'.length));
      return polygon ? [polygon] : null;
    }

    if (normalizedWkt.startsWith('MULTIPOLYGON')) {
      const innerMultiPolygon = stripOuterParens(normalizedWkt.slice('MULTIPOLYGON'.length));
      if (!innerMultiPolygon) return null;

      const polygons = splitTopLevelCommaSections(innerMultiPolygon)
        .map((polygonText) => parseWktPolygon(polygonText))
        .filter((polygon): polygon is WktPolygonCoordinates => Boolean(polygon));

      return polygons.length > 0 ? polygons : null;
    }
  } catch {
    return null;
  }

  return null;
};

/**
 * Calculates the bounding box of a polygon
 */
const getPolygonBounds = (coords: LngLat[]): BoundingBox => {
  let minLng = Infinity;
  let maxLng = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  coords.forEach((c) => {
    minLng = Math.min(minLng, c.lng);
    maxLng = Math.max(maxLng, c.lng);
    minLat = Math.min(minLat, c.lat);
    maxLat = Math.max(maxLat, c.lat);
  });

  return { minLng, maxLng, minLat, maxLat };
};

/**
 * Extracts bounding box from WKT polygon string
 */
export const getWKTBounds = (wkt: string): BoundingBox | null => {
  try {
    const polygons = parseWKTPolygons(wkt);
    if (!polygons) return null;

    let minLng = Infinity;
    let maxLng = -Infinity;
    let minLat = Infinity;
    let maxLat = -Infinity;

    polygons.forEach((polygonCoords) => {
      polygonCoords.forEach((ringCoords) => {
        ringCoords.forEach(([lng, lat]) => {
          minLng = Math.min(minLng, lng);
          maxLng = Math.max(maxLng, lng);
          minLat = Math.min(minLat, lat);
          maxLat = Math.max(maxLat, lat);
        });
      });
    });

    return { minLng, maxLng, minLat, maxLat };
  } catch {
    return null;
  }
};

/**
 * Checks if two bounding boxes overlap
 */
const doBoundsOverlap = (bbox1: BoundingBox, bbox2: BoundingBox): boolean => {
  return !(bbox1.maxLng < bbox2.minLng || bbox1.minLng > bbox2.maxLng || bbox1.maxLat < bbox2.minLat || bbox1.minLat > bbox2.maxLat);
};

/**
 * Checks if a WKT polygon intersects with a drawn polygon.
 * Uses optimized pre-filtering with centroid and bounding box checks before expensive intersection test.
 * @param wkt - WKT string representing a polygon (e.g., "POLYGON((lng lat, lng lat, ...))")
 * @param centroid - The polygon's centroid [lng, lat]
 * @param drawnPolygonCoords - Array of polygon vertices from the drawn polygon (LngLat objects)
 * @param drawnBounds - Pre-computed bounding box of the drawn polygon (optional, will compute if not provided)
 * @returns true if the WKT polygon intersects with the drawn polygon, false otherwise
 */
export const doesWKTPolygonIntersect = (wkt: string, centroid: [number, number], drawnPolygonCoords: LngLat[], drawnBounds?: BoundingBox): boolean => {
  if (!wkt || drawnPolygonCoords.length < 3) return false;

  // Pre-filter 1: Check if centroid is inside drawn polygon (fastest check)
  // If centroid is inside, the polygon definitely intersects
  const centroidInside = isPointInPolygon({ latitude: centroid[1], longitude: centroid[0] }, drawnPolygonCoords);
  if (centroidInside) {
    return true;
  }

  // Pre-filter 2: Check if bounding boxes overlap
  // If they don't overlap, polygons can't intersect
  const wktBounds = getWKTBounds(wkt);
  if (!wktBounds) return false;

  const bounds = drawnBounds || getPolygonBounds(drawnPolygonCoords);
  if (!doBoundsOverlap(wktBounds, bounds)) {
    return false;
  }

  // If we get here, bounding boxes overlap but centroid is outside
  // Need to do the expensive intersection check
  try {
    const wktPolygons = parseWKTPolygons(wkt);
    if (!wktPolygons) return false;

    const drawnCoords = [...drawnPolygonCoords.map((c) => [c.lng, c.lat]), [drawnPolygonCoords[0].lng, drawnPolygonCoords[0].lat]];
    const drawnPolygon = polygon([drawnCoords]);

    return wktPolygons.some((polygonCoords) => booleanIntersects(polygon(polygonCoords), drawnPolygon));
  } catch (error) {
    console.error('Error parsing WKT or checking intersection:', error);
    return false;
  }
};

/**
 * Counts point records per UTC day, keyed by the day's epoch ms, the shape the campaign timeline chart plots.
 */
export const countRecordsByDay = (data: EGESuperSummarizedPBFExt[] | EGESlimCachePBFResponseRow[]): Record<number, number> => {
  const counts: Record<number, number> = {};
  const add = (time: number) => {
    const day = dayjs.utc(time).startOf('day').valueOf();
    counts[day] = (counts[day] ?? 0) + 1;
  };
  if (isEGESuperSummarizedPBFExtArray(data)) {
    for (const d of data) add(d.epochms);
  } else {
    for (const d of data) add(d.recordCreatedTime);
  }
  return counts;
};
